/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'

import { OnChangeEventContext, OnInputChangeEventContext } from 'react-crane'
import { CatalogSearchResultFragment } from '../../../generated/graphql'
import useAppSettings from '../../../lib/hooks/useAppSettings'
import { DropdownProps } from '../../shared/input/Dropdown'
import { Autocomplete } from '../../shared/input'
import CourseOptionLabel from '../CourseOptionLabel'
import { PlanItemOption } from '../../../types'
import ChipFilterMultiSelect from '../../shared/input/ChipFilterMultiSelect/ChipFilterMultiSelect'
import { stringFormat } from '../../../lib/utils'

const containerCss = css`
  padding-bottom: 25px;
`

export type CourseAutoCompleteProps = {
  /** List of Catalog Courses returned from GraphQL API for dropdown */
  catalogCourses: Array<CatalogSearchResultFragment>
  /** If the selected course can be cleared */
  clearable?: boolean
  /** Current Term Code */
  groupId: string
  /** Input Value of Dropdown */
  inputValue: string
  /** If the dropdown should allow selecting multiple options */
  isMultiSelect?: boolean
  /** Label of Autocomplete. Defaults to Item */
  label?: string
  /** Optional override for placeholder */
  placeholder?: string
  /** Event callback when user changes input value in dropdown */
  onInputChange: (inputVal: string) => void
  /** Event callback when a new Catalog Course is selected or placeholder is added */
  onChange: (course: CatalogSearchResultFragment | CatalogSearchResultFragment[]) => void
  /** Whether to show static option at bottom of list */
  showStaticOption?: boolean
  /** Composite key of SubjectID and CourseNumber */
  value?: string | CatalogSearchResultFragment[]
} & Omit<DropdownProps, 'onChange' | 'onInputChange' | 'value'>

const defaultProps: Pick<
  CourseAutoCompleteProps,
  'clearable' | 'isMultiSelect' | 'label' | 'placeholder' | 'showStaticOption' | 'value'
> = {
  clearable: false,
  isMultiSelect: false,
  label: 'Item',
  placeholder: undefined,
  showStaticOption: false,
  value: ''
}

export const CourseAutoComplete = ({
  catalogCourses,
  clearable,
  disabled,
  groupId,
  inputValue,
  isMultiSelect,
  label,
  onChange,
  onInputChange,
  placeholder,
  showStaticOption,
  value,
  ...otherProps
}: CourseAutoCompleteProps) => {
  const { academicPlanningSettings, text } = useAppSettings()
  const { courseDisplayFormat } = academicPlanningSettings
  const { course, coursePlural } = text

  const handleChange = (e: OnChangeEventContext) => {
    const courseValue = e.value as CatalogSearchResultFragment
    onChange(courseValue)
  }
  const onStaticOptionClick = () => {
    onChange({
      courseNumber: '',
      creditsMax: 0,
      creditsMin: 0,
      description: '',
      externalCourseId: '',
      id: '',
      subjectId: '',
      title: inputValue
    })
  }
  const options = catalogCourses.map(
    (c) =>
      ({
        ...c,
        uniqueKey: `${c.externalCourseId}`,
        label: c ? stringFormat(courseDisplayFormat, c) : ''
      } as PlanItemOption)
  )

  const chipLabel = (option: any) => `${option.subjectId} ${option.courseNumber}`

  const handleMultiSelect = (selectedCourses: Array<CatalogSearchResultFragment>) => {
    onChange(selectedCourses)
  }

  const handleInputChange = (e: OnInputChangeEventContext) => {
    onInputChange(e.value)
  }

  const handleSearchChange = (searchOptions: string) => {
    onInputChange(searchOptions)
  }

  if (isMultiSelect) {
    return (
      <ChipFilterMultiSelect
        chipLabel={chipLabel}
        disabled={disabled}
        id={`group_${groupId}_new_item`}
        isSearchable
        label={label || coursePlural}
        labelKey="label"
        options={options}
        searchOptions={handleSearchChange}
        selectedOptions={(value as any) || []}
        setSelectedOptions={handleMultiSelect}
        valueKey="externalCourseId"
        valueLabelLimit={-1}
      />
    )
  }

  return (
    <div css={containerCss}>
      <Autocomplete
        {...otherProps}
        clearable={clearable}
        disabled={disabled}
        id={`group_${groupId}_new_item`}
        inputValue={inputValue}
        label={label || course}
        labelKey="label"
        noResultsText=""
        onChange={handleChange}
        onInputChange={handleInputChange}
        onStaticOptionClick={onStaticOptionClick}
        optionRenderer={CourseOptionLabel}
        options={options}
        placeholder={placeholder}
        showStaticOption={showStaticOption}
        value={value}
        valueKey="uniqueKey"
      />
    </div>
  )
}

CourseAutoComplete.defaultProps = defaultProps

export default CourseAutoComplete
